<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold d-flex align-items-center flex-wrap">
              <span class="mr-2">
                {{ obj.id ? `Invoice ${obj.invoiceNumber || ""}` : "Create invoice" }}
              </span>
              <CBadge
                :color="badgeColor('invoice', obj.statusDescription || 'Draft')"
                class="px-2 py-1"
              >
                {{ badgeLabel("invoice", obj.statusDescription || "Draft") }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? selectedCustomer && selectedCustomer.name
                    ? `For ${selectedCustomer.name}`
                    : "Invoice details"
                  : "Bill your customer for goods or services"
              }}
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CDropdown
              placement="bottom-end"
              toggler-text="Actions"
              color="light"
              class="mr-2"
            >
              <CDropdownItem @click="addNew()">New invoice</CDropdownItem>
              <template v-if="computedInvoiceStatuses.length">
                <CDropdownDivider />
                <CDropdownHeader>Change status</CDropdownHeader>
                <CDropdownItem
                  v-for="status in computedInvoiceStatuses"
                  :key="status.id || status.name"
                  @click="changeState(status)"
                >
                  {{ status.name === "Approve" ? "Approve invoice" : "Move back to draft" }}
                </CDropdownItem>
              </template>
            </CDropdown>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h5 mb-0">Invoice details</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <CAlert v-if="isApproved" color="info" class="mb-4">
              <CIcon name="cil-info" class="mr-2" />
              This invoice is approved and posted to your accounts. To change lines, tax or
              dates, move it back to Draft first. Payments can still be added.
            </CAlert>

            <!-- Customer & Invoice Info -->
            <CRow>
              <CCol md="6">
                <div class="form-group pt-2" ref="customerField">
                  <label for="invoice-customer">Customer *</label>
                  <v-select
                    ref="customerSelect"
                    input-id="invoice-customer"
                    style="width: 100%"
                    :class="{ 'is-invalid': !!errors.customer }"
                    v-model="selectedCustomer"
                    :label="'name'"
                    :options="customerItemsWithAddNew"
                    placeholder="Select customer"
                    @input="handleCustomerSelect"
                  >
                    <template #no-options>
                      <button type="button" class="btn btn-link p-0" @click="addNewCustomer">
                        Add new customer
                      </button>
                    </template>
                  </v-select>
                  <div v-if="errors.customer" class="invalid-feedback d-block">
                    {{ errors.customer }}
                  </div>
                </div>

                <div
                  v-if="selectedCustomer && selectedCustomer.id !== 'add_new'"
                  class="customer-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="c-avatar c-avatar-sm bg-primary text-white mr-2"
                    >
                      {{
                        selectedCustomer.name
                          ? selectedCustomer.name.charAt(0)
                          : "?"
                      }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedCustomer.name }}
                    </div>
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedCustomer.address"
                  >
                    <CIcon name="cil-location-pin" />
                    {{ selectedCustomer.address }}
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedCustomer.contactEmail"
                  >
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedCustomer.contactEmail }}
                  </div>
                  <div
                    class="small text-muted"
                    v-if="selectedCustomer.contactPhone"
                  >
                    <CIcon name="cil-phone" />
                    {{ selectedCustomer.contactPhone }}
                  </div>
                </div>

                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Contact person</template>
                  <template #input>
                    <CInput
                      placeholder="Contact person name"
                      v-model="obj.contactName"
                    />
                  </template>
                </CFormGroup>
              </CCol>

              <CCol md="6">
                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Invoice number</template>
                  <template #input>
                    <CInput
                      placeholder="Auto-generated if left empty"
                      v-model="obj.invoiceNumber"
                    />
                  </template>
                </CFormGroup>

                <CRow form class="form-group">
                  <CCol sm="6">
                    <label class="col-form-label">Issue date</label>
                    <div v-if="isApproved" class="py-2">
                      {{ formatDate(computeIssuedDate) }}
                    </div>
                    <CInput
                      v-else
                      type="date"
                      :value="computeIssuedDate"
                      @change="setIssuedDate"
                    />
                  </CCol>
                  <CCol sm="6">
                    <label class="col-form-label">Due date</label>
                    <CInput
                      type="date"
                      :value="computeExpiryDate"
                      @change="setExpiryDate"
                    />
                  </CCol>
                </CRow>

                <CRow form class="form-group">
                  <CCol sm="6" class="d-flex align-items-center">
                    <CSwitch
                      class="mr-2"
                      color="primary"
                      :checked.sync="showIssueDateInPrint"
                    />
                    <label class="mb-0 small text-muted">
                      <CIcon name="cil-print" size="sm" class="mr-1" />
                      Show issue date in print
                    </label>
                  </CCol>
                  <CCol sm="6" class="d-flex align-items-center">
                    <CSwitch
                      class="mr-2"
                      color="primary"
                      :checked.sync="showDueDateInPrint"
                    />
                    <label class="mb-0 small text-muted">
                      <CIcon name="cil-print" size="sm" class="mr-1" />
                      Show due date in print
                    </label>
                  </CCol>
                </CRow>

                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Reference</template>
                  <template #input>
                    <CInput
                      placeholder="PO number or reference"
                      v-model="obj.reference"
                    />
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CFormGroup>
              <template #label>Invoice title</template>
              <template #input>
                <CTextarea
                  v-model="obj.title"
                  placeholder="Enter a descriptive title for this invoice"
                  rows="2"
                />
              </template>
            </CFormGroup>

            <!-- Invoice Items -->
            <div ref="itemsField">
              <CAlert v-if="errors.items" color="danger" class="mb-3" role="alert">
                {{ errors.items }}
              </CAlert>
            </div>
            <InvoiceItemsComponent
              :items="invoiceItems"
              :productItems="computeProductItemsWithAddNew"
              :grandTotal="grandTotal"
              :grandTaxOnly="grandTaxOnly"
              :grandTotalWithTax="grandTotalWithTax"
              :selectedSalesTax="selectedSalesTax"
              :salesTaxItems="salesTaxItemsWithAddNew"
              itemType="invoice"
              :showTax="true"
              :useDescriptionModal="true"
              :readonly="isApproved"
              @add-item="addNewItem"
              @remove-item="onRemoveClaimItem"
              @move-item="moveItem"
              @product-selected="handleProductSelect"
              @tax-selected="handleSalesTaxSelect"
              @add-new-tax="addNewSalesTax"
            />

            <!-- Payment Tracking Section (Only when Approved) -->
            <div v-if="obj.status >= 1" class="payment-section mt-4">
              <InvoicePaymentsComponent
                :payments="obj.payments || []"
                :billTotal="grandTotalWithTax"
                :isReadOnly="false"
                :enableReceipt="true"
                @add-payment="addPayment"
                @remove-payment="removePayment"
                @payment-change="onPaymentChange"
                @generate-receipt="onGenerateReceipt"
              />
            </div>

            <!-- Terms & Conditions -->
            <div class="mt-4">
              <CTextarea
                label="Terms and conditions"
                v-model="obj.note"
                placeholder="Enter your terms and conditions"
                rows="10"
              />
            </div>
          </CCardBody>
          <CCardFooter
            class="bg-transparent d-flex justify-content-between p-4"
          >
            <CButton color="light" :disabled="saving" @click="cancel"> Cancel </CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              <CIcon v-else name="cil-check-circle" />
              {{ obj.id ? "Save" : "Create invoice" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Preview Section -->
    <CRow>
      <CCol lg="12">
        <CCard class="border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h5 mb-0">Preview</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <WidgetsReportInvoice
              :invoice="computedPreviewItem"
              :showIssueDate="showIssueDateInPrint"
              :showDueDate="showDueDateInPrint"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Add Customer / Product Modals (shared) -->
    <CustomerFormModal
      :show="addCustomerFormPopup"
      :customer="itemAddNewCustomer"
      @close="addCustomerFormPopup = false"
      @save="saveNewCustomer"
    />

    <ProductFormModal
      :show="addPoductFormPopup"
      :product="itemAddNewProduct"
      @close="onProductModalClose"
      @save="saveNewProduct"
    />

    <!-- Add Sales Tax Modal -->
    <SalesTaxFormModal
      :show="addSalesTaxFormPopup"
      :salesTax="itemAddNewSalesTax"
      @close="addSalesTaxFormPopup = false"
      @save="onSalesTaxPopupConfirmation"
    />

    <!-- Status Change Confirmation (approving posts to the ledger) -->
    <CModal
      :title="statusConfirmTitle"
      :show.sync="statusConfirmModal"
      :close-on-backdrop="!changingState"
      @update:show="onStatusConfirmModalClose"
    >
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="cancelStatusChange">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="changingState" @click="confirmStatusChange">
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ statusConfirmButton }}
        </CButton>
      </template>
    </CModal>

    <!-- Chart of Account Modal -->
    <CModal
      title="Choose an account"
      size="lg"
      :show.sync="chooseAccountPopup"
      @update:show="onAccountPopupConfirmation"
    >
      <CRow>
        <CCol md="12">
          <CFormGroup>
            <CLabel class="font-weight-bold">Account *</CLabel>
            <v-select
              v-model="selectedChartAccount"
              :label="`itemDisplay`"
              :options="filteredChartAccounts"
              placeholder="Select an account"
            />
          </CFormGroup>
        </CCol>
      </CRow>
      <template #footer>
        <CButton color="light" @click="chooseAccountPopup = false">
          Cancel
        </CButton>
        <CButton
          color="primary"
          :disabled="!selectedChartAccount"
          @click="confirmAccountSelection"
        >
          Use account
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import InvoiceApi from "@/lib/invoiceApi";
import CustomerApi from "@/lib/customerApi";
import IncomeReceiptApi from "@/lib/incomeReceiptApi";
import ProductApi from "@/lib/productApi";
import SalesTaxApi from "@/lib/salesTaxApi";
import BusinessApi from "@/lib/businessApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import QuotationApi from "@/lib/quotationApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, addDays, toApiDate, asDateString, formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import WidgetsReportInvoice from "../widgets/WidgetsReportInvoice";
import InvoiceItemsComponent from "../components/InvoiceItemsComponent";
import SalesTaxFormModal from "../components/SalesTaxFormModal";
import CustomerFormModal from "../components/CustomerFormModal";
import ProductFormModal from "../components/ProductFormModal";
import InvoicePaymentsComponent from "../components/BillPaymentsComponent";

export default {
  name: "Invoice",
  components: {
    vSelect,
    WidgetsReportInvoice,
    InvoiceItemsComponent,
    SalesTaxFormModal,
    CustomerFormModal,
    ProductFormModal,
    InvoicePaymentsComponent,
  },
  data: () => {
    return {
      chooseAccountPopup: false,
      saving: false,
      errors: { customer: "", items: "" },
      statusConfirmModal: false,
      changingState: false,
      selectedChartAccount: null,
      pendingStatusChange: null,
      filteredChartAccounts: [],
      chartOfAccountItems: [],
      chartOfAccountApi: new ChartOfAccountApi(),

      itemPendingNewProduct: null,
      itemAddNewProduct: {},
      addPoductFormPopup: false,

      itemAddNewCustomer: {},
      addCustomerFormPopup: false,

      itemAddNewSalesTax: {},
      addSalesTaxFormPopup: false,

      businessApi: new BusinessApi(),
      quotationApi: new QuotationApi(),

      invoiceStatuses: [],
      issuedDate: null,
      expiryDate: null,

      // Display settings for print/preview
      showIssueDateInPrint: true,
      showDueDateInPrint: true,

      // Invoice Items
      invoiceItems: [],

      selectedItem: null,
      selectedCustomer: null,
      selectedSalesTax: null,
      organizationTypeList: [],
      obj: {
        invoiceNumber: "",
      },
      submitted: false,
      api: new InvoiceApi(),
      customerApi: new CustomerApi(),
      incomeReceiptApi: new IncomeReceiptApi(),
      generatingReceiptForId: null,
      salesTaxApi: new SalesTaxApi(),
      productApi: new ProductApi(),
      customerItems: [],
      salesTaxItems: [],
      productItems: [],
    };
  },
  mounted() {
    var self = this;
    this.fetchInvoiceStatuses();
    self.refreshCustomer();
    self.refreshProduct();
    self.refreshSalesTax();
    this.fetchChartOfAccount();

    if (self.$route.name === "InvoiceConvertFromQuot") {
      this.processInvoiceConvertFromQuot();
    } else if (self.$route.name === "InvoiceConvertFromItem") {
      this.processInvoiceConvertFromItem();
    } else {
      self.resetObj();
    }
  },
  computed: {
    // A saved, approved invoice is posted to the ledger: the API refuses changes to its
    // lines, tax, issue date or business until it is moved back to Draft.
    isApproved() {
      return !!this.obj.id && this.obj.statusDescription === "Approve";
    },
    isPendingApprove() {
      return !!this.pendingStatusChange && this.pendingStatusChange.name === "Approve";
    },
    isPendingDraft() {
      return !!this.pendingStatusChange && this.pendingStatusChange.name === "Draft";
    },
    statusConfirmTitle() {
      const ref = this.obj.invoiceNumber ? ` ${this.obj.invoiceNumber}` : "";
      if (this.isPendingApprove) return `Approve invoice${ref}?`;
      if (this.isPendingDraft) return `Move invoice${ref} back to draft?`;
      const name = this.pendingStatusChange ? badgeLabel("invoice", this.pendingStatusChange.name) : "";
      return `Change invoice${ref} to ${name}?`;
    },
    statusConfirmMessage() {
      const ref = this.obj.invoiceNumber ? ` ${this.obj.invoiceNumber}` : "";
      const amount = `RM ${formatMyr(this.grandTotalWithTax)}`;
      if (this.isPendingApprove)
        return `Invoice${ref} for ${amount} will be approved and posted to your accounts.`;
      if (this.isPendingDraft)
        return `The posting of invoice${ref} for ${amount} will be reversed in your accounts. You can then change it and approve it again.`;
      const name = this.pendingStatusChange ? badgeLabel("invoice", this.pendingStatusChange.name) : "";
      return `Invoice${ref} for ${amount} will be changed to ${name}.`;
    },
    statusConfirmButton() {
      if (this.isPendingApprove) return "Approve invoice";
      if (this.isPendingDraft) return "Move to draft";
      return "Change status";
    },

    computedInvoiceStatuses() {
      let statuses = this.invoiceStatuses.filter(
        (status) => status.name != this.obj.statusDescription
      );

      if (this.obj.statusDescription === "Draft")
        return statuses.filter((status) => status.name == "Approve");
      if (this.obj.statusDescription === "Approve")
        return statuses.filter((status) => status.name == "Draft");
      return [];
    },

    computeProductItemsWithAddNew() {
      return [...this.productItems, { id: "add_new", name: "+ Add new product" }];
    },

    customerItemsWithAddNew() {
      return [...this.customerItems, { id: "add_new", name: "+ Add new customer" }];
    },

    salesTaxItemsWithAddNew() {
      return [...this.salesTaxItems, { id: "add_new", name: "+ Add new sales tax" }];
    },

    computedPreviewItem() {
      return {
        ...this.obj,
        items: this.computedInvoiceItems,
        expiryDate: this.expiryDate,
        salesTax: this.selectedSalesTax,
      };
    },

    computeExpiryDate() {
      return asDateString(this.expiryDate);
    },

    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },

    computedInvoiceItems() {
      return this.invoiceItems.map((item) => {
        return {
          ...item,
          productName: item.product?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },

    grandTotalWithTax() {
      return this.grandTotal + this.grandTaxOnly;
    },

    grandTaxOnly() {
      try {
        return (
          (this.selectedSalesTax?.rateInPercentage * this.grandTotal) / 100 || 0
        );
      } catch (error) {
        return 0;
      }
    },

    grandTotal() {
      return this.invoiceItems.reduce((total, item) => {
        return (
          total +
          (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0)
        );
      }, 0);
    },
  },
  watch: {
    invoiceItems: {
      handler() {
        if (this.errors.items) this.errors.items = "";
      },
      deep: true,
    },
  },
  methods: {
    formatDate,
    badgeColor,
    badgeLabel,

    getTaxName() {
      try {
        return this.selectedSalesTax?.name || "0%";
      } catch (error) {
        return "0%";
      }
    },

    fetchChartOfAccount() {
      var self = this;
      self.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.chartOfAccountItems = response.result;
        })
        .catch(({ data }) => {});
    },

    // Event handlers
    handleCustomerSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewCustomer();
        this.selectedCustomer = null;
      } else {
        this.selectedCustomer = selected;
        if (selected) this.errors.customer = "";
      }
    },

    handleProductSelect(selected, item) {
      if (selected?.id === "add_new") {
        this.itemPendingNewProduct = item;
        this.itemAddNewProduct = {};
        this.addPoductFormPopup = true;
      } else {
        item.product = selected;
        if (selected && selected.defaultPrice) {
          item.price = selected.defaultPrice;
        }
        if (selected && selected.defaultUnit) {
          item.unit = selected.defaultUnit;
        }
      }
    },

    handleSalesTaxSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewSalesTax();
        this.selectedSalesTax = null;
      } else {
        this.selectedSalesTax = selected;
      }
    },

    // Modal handlers
    addNewCustomer() {
      this.itemAddNewCustomer = {};
      this.addCustomerFormPopup = true;
    },

    addNewSalesTax() {
      this.itemAddNewSalesTax = {};
      this.addSalesTaxFormPopup = true;
    },

    onProductModalClose() {
      this.addPoductFormPopup = false;
      if (this.itemPendingNewProduct && this.itemPendingNewProduct.product && this.itemPendingNewProduct.product.id === "add_new") {
        this.itemPendingNewProduct.product = null;
      }
    },

    saveNewProduct(product) {
      this.productApi
        .create(product)
        .then((response) => {
          const addedProduct = response.result;
          this.refreshProduct();
          if (this.itemPendingNewProduct) {
            this.itemPendingNewProduct.product = addedProduct;
          }
          this.$toast.success("Product added");
          this.addPoductFormPopup = false;
          this.itemAddNewProduct = {};
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't add the product. ${helper.getErrorMessage(data)}`);
        });
    },

    saveNewCustomer(customer) {
      this.customerApi
        .create(customer)
        .then((response) => {
          const addedCustomer = response.result;
          this.refreshCustomer();
          this.selectedCustomer = addedCustomer;
          this.errors.customer = "";
          this.$toast.success("Customer added");
          this.addCustomerFormPopup = false;
          this.itemAddNewCustomer = {};
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't add the customer. ${helper.getErrorMessage(data)}`);
        });
    },

    // SalesTaxFormModal emits `save` with the new tax.
    onSalesTaxPopupConfirmation(salesTax) {
      this.salesTaxApi
        .create(salesTax)
        .then((response) => {
          const addedSalesTax = response.result;
          this.refreshSalesTax();
          this.selectedSalesTax = addedSalesTax;
          this.$toast.success("Sales tax added");
          this.addSalesTaxFormPopup = false;
          this.itemAddNewSalesTax = {};
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't add the sales tax. ${helper.getErrorMessage(data)}`);
        });
    },

    addNew() {
      this.$router.push({ path: "/tenants/Invoice" });
    },

    cancel() {
      this.$router.push({ path: "/tenants/InvoiceList" });
    },

    changeState(item) {
      if (!this.obj.id) {
        this.$toast.warning("Save the invoice before changing its status");
        return;
      }
      this.pendingStatusChange = item;
      this.statusConfirmModal = true;
    },

    confirmStatusChange() {
      this.proceedChangeState(null);
    },

    cancelStatusChange() {
      this.statusConfirmModal = false;
      this.pendingStatusChange = null;
    },

    onStatusConfirmModalClose(visible) {
      if (!visible && !this.changingState) this.pendingStatusChange = null;
    },

    onAccountPopupConfirmation(visible) {
      this.chooseAccountPopup = visible;
    },

    confirmAccountSelection() {
      this.proceedChangeState(this.selectedChartAccount);
      this.selectedChartAccountId = null;
    },

    proceedChangeState(account) {
      var self = this;
      if (self.changingState || !self.pendingStatusChange) return;
      const previousStatus = self.obj.status;
      self.obj.status = self.pendingStatusChange.id;
      let accountId = null;
      if (account != null) accountId = account.id;

      if (self.obj.id) {
        self.changingState = true;
        this.api
          .updateState(self.obj, accountId)
          .then((response) => {
            const approved = self.isPendingApprove;
            self.resetObj();
            self.$toast.success(approved ? "Invoice approved" : "Invoice moved back to draft");
          })
          .catch((error) => {
            // Nothing changed on the server (e.g. the invoice has payments, or its SST
            // period is closed): keep the old status and show the API's sentence.
            self.obj.status = previousStatus;
            console.error("[Invoice] status change failed:", error);
            self.$toast.error(
              `Couldn't change the invoice status. ${helper.getErrorMessage(error && error.data)}`
            );
          })
          .finally(() => {
            this.chooseAccountPopup = false;
            this.statusConfirmModal = false;
            this.changingState = false;
            this.pendingStatusChange = null;
          });
      }
    },

    fetchInvoiceStatuses() {
      var self = this;
      self.api
        .getInvoiceStatus()
        .then((response) => {
          this.invoiceStatuses = response.result;
        })
        .catch((error) => {
          console.error("[Invoice] load statuses failed:", error);
          self.$toast.error("Couldn't load the invoice statuses. Refresh the page to try again.");
        });
    },

    setExpiryDate(e) {
      this.expiryDate = e;
    },

    setIssuedDate(e) {
      this.issuedDate = e;
    },

    // Payment tracking methods
    addPayment() {
      if (!this.obj.payments) {
        this.obj.payments = [];
      }

      const newPayment = {
        id: this.generateGUID(),
        paymentDate: mytToday(),
        amount: Math.max(0, this.grandTotalWithTax - this.computeTotalPaid()),
        paymentMethod: "Bank Transfer",
        paymentReference: "",
        paymentNotes: "",
      };

      this.obj.payments.push(newPayment);
    },

    removePayment(index) {
      if (this.obj.payments && this.obj.payments.length > index) {
        this.obj.payments.splice(index, 1);
      }
    },

    onPaymentChange(payment) {
      // Payment data is already bound via v-model in the component
      // This method can be used for additional logic if needed
    },

    // Generate an Official Receipt (IncomeReceipt) from a saved invoice payment,
    // then download its PDF. Idempotent server-side (one receipt per payment).
    async onGenerateReceipt(payment) {
      var self = this;

      if (!payment || !payment.isSaved || !self.obj.id) {
        self.$toast.warning("Save the invoice before generating a receipt for this payment");
        return;
      }

      if (self.generatingReceiptForId) return; // guard against double-clicks
      self.generatingReceiptForId = payment.id;

      try {
        const response = await self.incomeReceiptApi.createFromPayment(
          self.obj.id,
          payment.id
        );
        const receipt = response.result;
        if (!receipt || !receipt.id) {
          throw new Error("No receipt was returned by the server.");
        }

        const filename = `${receipt.incomeReceiptNumber || "receipt"}.pdf`;
        const ok = await self.incomeReceiptApi.downloadPDF(receipt.id, filename);
        if (ok) {
          self.$toast.success(`Official receipt ${receipt.incomeReceiptNumber} generated`);
        } else {
          self.$toast.warning(
            `Receipt ${receipt.incomeReceiptNumber} was created, but the PDF didn't download. Try again from the receipts list`
          );
        }
      } catch (err) {
        console.error("[Invoice] generate receipt failed:", err);
        const detail = err && err.data ? ` ${helper.getErrorMessage(err.data)}` : " Please try again.";
        self.$toast.error(`Couldn't generate the receipt.${detail}`);
      } finally {
        self.generatingReceiptForId = null;
      }
    },

    computeTotalPaid() {
      if (!this.obj.payments || this.obj.payments.length === 0) return 0;
      return this.obj.payments.reduce((sum, p) => {
        return sum + (parseFloat(p.amount) || 0);
      }, 0);
    },

    updatePositions() {
      this.invoiceItems.forEach((item, index) => {
        item.position = index + 1;
      });
    },

    moveItem(index, direction) {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex >= 0 && newIndex < this.invoiceItems.length) {
        const temp = this.invoiceItems[index];
        this.$set(this.invoiceItems, index, this.invoiceItems[newIndex]);
        this.$set(this.invoiceItems, newIndex, temp);
        this.updatePositions();
      }
    },

    onRemoveClaimItem(item) {
      const index = this.invoiceItems.findIndex((i) => i.id === item.id);
      if (index > -1) {
        this.invoiceItems.splice(index, 1);
        this.updatePositions();
      }
    },

    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },

    addNewItem() {
      const newPosition = this.invoiceItems.length + 1;
      this.invoiceItems.push({
        id: this.generateGUID(),
        product:
          this.productItems.length > 0 ? this.productItems[0] : { name: "" },
        price: 0,
        quantity: 1,
        unit: "L.S.",
        description: "",
        position: newPosition,
      });
    },

    getTotalItemPrice(item) {
      try {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        return quantity * price;
      } catch (error) {
        return 0;
      }
    },

    refreshProduct() {
      var self = this;
      self.productApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.productItems = response.result;
        })
        .catch(({ data }) => {});
    },

    refreshCustomer() {
      var self = this;
      self.customerApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.customerItems = response.result;
        })
        .catch(({ data }) => {});
    },

    refreshSalesTax() {
      this.salesTaxApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.salesTaxItems = response.result;
        })
        .catch((error) => {
          console.error('[Invoice] refreshSalesTax - Error loading sales tax:', error);
          this.salesTaxItems = [];
        });
    },

    processInvoiceConvertFromItem() {
      var self = this;
      self.quotationApi
        .getItem(self.$route.params.id)
        .then((response) => {
          self.obj = self.getEmptyObj();
          var item = response.result;
          this.issuedDate = mytToday();
          this.expiryDate = addDays(this.issuedDate, 30);

          self.selectedCustomer = item.quotation.customer;
          self.selectedSalesTax = item.quotation.salesTax;

          this.invoiceItems.push({
            id: this.generateGUID(),
            product: item.product,
            price: item.price,
            quantity: item.quantity,
            unit: item.unit,
            description: item.description,
            position: 1,
          });

          this.api
            .getNextNumber()
            .then((response) => {
              self.obj.invoiceNumber = response.result;
            })
            .catch(({ data }) => {});

          this.toast(
            "Info",
            "Converting a quotation item to an invoice",
            "info"
          );
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load the invoice. ${helper.getErrorMessage(data)}`, "danger");
        });
    },

    processInvoiceConvertFromQuot() {
      var self = this;
      self.quotationApi
        .get(self.$route.params.id)
        .then((response) => {
          self.obj = self.getEmptyObj();
          var quotation = response.result;
          this.issuedDate = mytToday();
          this.expiryDate = addDays(this.issuedDate, 30);

          self.selectedCustomer = quotation.customer;
          self.selectedSalesTax = quotation.salesTax;

          if (quotation.items.length > 0) {
            if (quotation.items[0].position == 0) {
              for (let i = 0; i < quotation.items.length; i++) {
                quotation.items[i].position = i + 1;
              }
            }
          }

          self.invoiceItems = quotation.items;

          this.api
            .getNextNumber()
            .then((response) => {
              self.obj.invoiceNumber = response.result;
            })
            .catch(({ data }) => {});

          for (let i = 0; i < self.invoiceItems.length; i++) {
            delete self.invoiceItems[i].id;
          }

          this.toast(
            "Info",
            "Converting the quotation to an invoice",
            "info"
          );
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load the invoice. ${helper.getErrorMessage(data)}`, "danger");
        });
    },

    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            this.issuedDate = self.obj.issuedDate;
            this.expiryDate = self.obj.dueDate;
            self.selectedCustomer = self.obj.customer;
            self.selectedSalesTax = self.obj.salesTax;

            // Initialize payments array if not exists
            if (!self.obj.payments) {
              self.obj.payments = [];
            }

            // Mark server-loaded payments as saved so the "Official Receipt"
            // action is only enabled for payments that exist server-side.
            self.obj.payments.forEach((p) => {
              p.isSaved = true;
            });

            // Load display settings (default to true for backwards compatibility)
            self.showIssueDateInPrint = self.obj.showIssueDateInPrint !== undefined
              ? self.obj.showIssueDateInPrint
              : true;
            self.showDueDateInPrint = self.obj.showDueDateInPrint !== undefined
              ? self.obj.showDueDateInPrint
              : true;

            if (self.obj.items.length > 0) {
              if (self.obj.items[0].position == 0) {
                for (let i = 0; i < self.obj.items.length; i++) {
                  self.obj.items[i].position = i + 1;
                }
              }
            }

            self.invoiceItems = self.obj.items;

            // Handle duplication logic
            if (self.$route.name === "InvoiceDuplicate") {
              delete self.obj.id;

              // A duplicated invoice is a fresh, unpaid document — never carry over
              // the source invoice's payments (their existing PKs would collide on insert).
              self.obj.payments = [];
              self.obj.paymentDate = null;

              this.issuedDate = mytToday();
              this.expiryDate = addDays(this.issuedDate, 30);

              // Get new invoice number
              this.api
                .getNextNumber()
                .then((response) => {
                  self.obj.invoiceNumber = response.result;
                })
                .catch(({ data }) => {});

              // Remove IDs from items to create new items
              if (self.invoiceItems && self.invoiceItems.length > 0) {
                self.invoiceItems = self.invoiceItems.map(item => {
                  const newItem = { ...item };
                  delete newItem.id;
                  return newItem;
                });
              }

              self.toast(
                "Info",
                "Duplicate invoice. Change what you need, then save",
                "info"
              );
            }
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the invoice. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();

        this.issuedDate = mytToday();
        this.expiryDate = addDays(this.issuedDate, 30);

        this.api
          .getNextNumber()
          .then((response) => {
            self.obj.invoiceNumber = response.result;
          })
          .catch(({ data }) => {});

        this.businessApi
          .getCurrentActiveBusiness()
          .then((response) => {
            self.obj.note = response.result.invoiceDefaultFooter;
          })
          .catch(({ data }) => {});
      }
    },

    onSubmit() {
      var self = this;
      if (self.saving) return;
      self.obj.items = self.computedInvoiceItems;

      // Date-only fields go to the API as "yyyy-MM-dd" (never toISOString: it shifts by 8 hours)
      self.obj.issuedDate = toApiDate(asDateString(self.issuedDate)) || null;
      self.obj.dueDate = toApiDate(asDateString(self.expiryDate)) || null;

      // Save display settings
      self.obj.showIssueDateInPrint = self.showIssueDateInPrint;
      self.obj.showDueDateInPrint = self.showDueDateInPrint;

      // Ensure payments array exists
      if (!self.obj.payments) {
        self.obj.payments = [];
      }

      let isItemsInvalid = false;

      self.obj.items.forEach((item) => {
        if (!item.product || !item.product.id) {
          isItemsInvalid = true;
          return;
        }
        item.productId = item.product.id;
      });

      if (self.selectedCustomer) {
        self.obj.customerId = self.selectedCustomer.id;
      }

      if (self.selectedSalesTax) {
        self.obj.salesTaxId = self.selectedSalesTax.id;
      }

      // Validate inline (no toasts); focus the first problem.
      self.errors = { customer: "", items: "" };
      if (self.obj.customerId == null) {
        self.errors.customer = "Choose a customer, or add a new one.";
      }
      if (self.obj.items.length == 0) {
        self.errors.items = "Add at least one line item.";
      } else if (isItemsInvalid) {
        self.errors.items = "Choose a product for every line item, or remove the empty line.";
      } else if (self.grandTotal == 0) {
        self.errors.items = "The invoice total is RM 0.00. Enter a price and quantity for at least one line.";
      }
      if (self.errors.customer || self.errors.items) {
        self.$nextTick(() => self.focusFirstError());
        return;
      }

      self.saving = true;
      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.$toast.success("Invoice created");
            var newObj = response.result;
            self.$router.push({
              path: `/tenants/Invoice/${newObj.id}`,
            });
          })
          .catch((error) => {
            console.error("[Invoice] create failed:", error);
            self.$toast.error(`Couldn't save the invoice. ${helper.getErrorMessage(error && error.data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            self.$toast.success("Invoice saved");
            self.resetObj();
          })
          .catch((error) => {
            // e.g. an approved invoice whose posted lines, tax or date changed: the API
            // explains what to do (move it back to Draft first).
            console.error("[Invoice] update failed:", error);
            self.$toast.error(`Couldn't save the invoice. ${helper.getErrorMessage(error && error.data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },

    focusFirstError() {
      if (this.errors.customer) {
        const field = this.$refs.customerField;
        if (field && field.scrollIntoView) field.scrollIntoView({ block: "center" });
        const input = field && field.querySelector("input");
        if (input) input.focus();
      } else if (this.errors.items) {
        const field = this.$refs.itemsField;
        if (field && field.scrollIntoView) field.scrollIntoView({ block: "center" });
      }
    },

    // UI Methods — global toasts (src/plugins/toast.js)
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning", info: "info" }[color] || "info";
      this.$toast[kind](message);
    },

    getEmptyObj() {
      return {
        invoiceNumber: "",
        items: [],
        payments: [],
      };
    },

    submit() {
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>

<style scoped>
.customer-details {
  transition: all 0.3s ease;
}

/* Custom styling for mobile view */
@media (max-width: 767.98px) {
  .table-responsive {
    display: none;
  }
}

@media (min-width: 768px) {
  .d-md-none {
    display: none;
  }
}
</style>
